import React from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { Eyebrow } from '../ui/kit';

/**
 * Account section chrome.
 *
 * The rail is a typographic index, not a second app sidebar: no icons, no
 * filled pills, no card chrome. A single hairline spine runs the length of the
 * list and thickens to ink on the current page — the same move the marketing
 * site uses for a table of contents. Grouped by what the row actually settles
 * (what you pay, where the site lives, who you are), because six flat rows read
 * as a dumped list of screens.
 */

interface MenuItem {
  id: string;
  label: string;
  path: string;
  /** One-line answer to "what do I change here?" — desktop rail only. */
  note: string;
}

interface MenuGroup {
  label: string;
  items: MenuItem[];
}

const menuGroups: MenuGroup[] = [
  {
    label: 'Subscription',
    items: [
      {
        id: 'billing',
        label: 'Billing',
        path: '/dashboard/account/billing',
        note: 'Plan, invoices, payment',
      },
    ],
  },
  {
    label: 'Website',
    items: [
      {
        id: 'domain',
        label: 'Domain',
        path: '/dashboard/account/domain',
        note: 'Your address on the web',
      },
    ],
  },
  {
    label: 'You',
    items: [
      {
        id: 'profile',
        label: 'Profile',
        path: '/dashboard/account/profile',
        note: 'Photo, name, timezone',
      },
      {
        id: 'password',
        label: 'Password',
        path: '/dashboard/account/password',
        note: 'Sign-in security',
      },
      {
        id: 'notifications',
        label: 'Notifications',
        path: '/dashboard/account/notifications',
        note: 'What reaches you, and how',
      },
      {
        id: 'activity',
        label: 'Activity',
        path: '/dashboard/account/activity',
        note: 'Recent account events',
      },
    ],
  },
];

const flatItems = menuGroups.flatMap(group => group.items);

const AccountLayout: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Mobile: one hairline row of labels. Same language, laid on its side. */}
      <div className="lg:hidden">
        <div className="border-b border-zinc-200">
          <nav className="flex overflow-x-auto hide-scrollbar -mb-px">
            {flatItems.map(item => (
              <NavLink
                key={item.id}
                to={item.path}
                className={({ isActive }) =>
                  `whitespace-nowrap border-b-2 px-4 py-3 text-sm transition-colors ${
                    isActive
                      ? 'border-primary font-medium text-primary'
                      : 'border-transparent text-muted hover:border-zinc-300 hover:text-primary'
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>

      {/* Desktop: index rail + content */}
      <div className="lg:flex gap-10 min-h-[calc(100vh-12rem)]">
        <div className="hidden lg:block w-52 flex-shrink-0">
          {/*
            No eyebrow above the rail — every page in this section already opens
            with one. The top padding drops the first row onto the title's line.
          */}
          <nav className="pt-7">
            {menuGroups.map((group, index) => (
              <div key={group.label} className={index > 0 ? 'mt-7' : ''}>
                <Eyebrow className="mb-2 pl-4 text-zinc-400">
                  {group.label}
                </Eyebrow>
                <div className="border-l border-zinc-200">
                  {group.items.map(item => (
                    <NavLink
                      key={item.id}
                      to={item.path}
                      className={({ isActive }) =>
                        `-ml-px block border-l-2 py-1.5 pl-4 pr-2 text-[14px] leading-snug transition-colors duration-150 ${
                          isActive
                            ? 'border-primary font-medium text-primary'
                            : 'border-transparent text-muted hover:border-zinc-300 hover:text-primary'
                        }`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span className="block">{item.label}</span>
                          {/* The note is a whisper: present when you're reading
                              the rail, invisible when you're not. */}
                          <span
                            className={`mt-0.5 block text-[11.5px] leading-tight ${
                              isActive ? 'text-zinc-500' : 'text-zinc-400'
                            }`}
                          >
                            {item.note}
                          </span>
                        </>
                      )}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </div>

        {/* Right Content */}
        <div className="flex-1 min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AccountLayout;
